import React, { useState, useContext } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Checkbox,
  Tooltip,
  Button,
  LinearProgress,
  Collapse,
  Grid,
  Divider
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  BookmarkBorder as BookmarkIcon,
  Bookmark as BookmarkedIcon,
  Psychology as PsychologyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ThumbUp,
  ThumbDown,
} from '@mui/icons-material';
import PaperReasoningDialog from '../shared/PaperReasoningDialog';
import SocketContext from '../../contexts/SocketContext';
import { downloadBibtex } from '../../utils/citationUtils';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

function PaperCard({
  paper,
  selected,
  onSelect,
  onOpenUrl,
  showAbstract = true,
  index = 0
}) {

  const [reasoningDialogOpen, setReasoningDialogOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isReasoningExpanded, setIsReasoningExpanded] = useState(true);

  // Use direct context to avoid the hook (which might be undefined)
  const socketContext = useContext(SocketContext);

  // Safely access socket data with fallbacks
  const socketId = socketContext?.socketId;
  const processingSteps = socketContext?.processingSteps || {};

  // Check if reasoning is available from the paper_reranking step
  const reasoningAvailable = processingSteps?.paper_reranking?.data?.reasoningAvailable || false;

  // Get paper score and reasoning data from the paper object or context
  const score = paper.score !== undefined ? paper.score :
    (processingSteps?.paper_reranking?.data?.scores ?
      processingSteps.paper_reranking.data.scores[paper.paperId || index]?.score : undefined);

  const reasoning = paper.reasoning ||
    (processingSteps?.paper_reranking?.data?.scores ?
      processingSteps.paper_reranking.data.scores[paper.paperId || index]?.reasoning : undefined);

  // Replace the sample data fallbacks with informative messages
  const showScore = score !== undefined ? score : 0; // Use 0 as neutral/uncertain score when missing

  const fallbackReasoning = {
    argumentsFor: "Unable to generate arguments supporting relevance. This could be due to:\n- API limitations\n- Processing error\n- Insufficient context\n\nPlease try refreshing or contact support if this persists.",
    argumentsAgainst: "Unable to generate arguments against relevance. This could be due to:\n- API limitations\n- Processing error\n- Insufficient context\n\nPlease try refreshing or contact support if this persists."
  };
  const showReasoning = reasoning || fallbackReasoning;

  // Add a new section for AI Reasoning if the paper has rankingData
  const hasRankingData = paper.rankingData && (paper.rankingData.argumentsFor || paper.rankingData.argumentsAgainst);

  if (!paper) {
    console.error('Paper object is undefined');
    return null;
  }

  const handleExportCitation = async (e) => {
    e.stopPropagation(); // Prevent card selection/expansion
    try {
      const filename = `${(paper.authors?.[0]?.name || 'Unknown').split(' ').pop()}${paper.year || 'ND'}.bib`;
      await downloadBibtex([paper], filename);
    } catch (error) {
      console.error('Error exporting citation:', error);
    }
  };

  const handleOpenReasoningDialog = () => {
    if (!socketId) {
      console.warn('Cannot open reasoning dialog: Socket ID is not available');
      return;
    }
    setReasoningDialogOpen(true);
  };

  const handleCloseReasoningDialog = () => {
    setReasoningDialogOpen(false);
  };

  const toggleExpansion = () => {
    setIsExpanded(prev => !prev);
  };

  const toggleReasoningExpansion = () => {
    setIsReasoningExpanded(prev => !prev);
  };

  // Format argumentsFor and argumentsAgainst as bullet points
  const formatArguments = (text) => {
    if (!text) return 'No data available';

    // If the text already has line breaks and hyphens, preserve that format
    if (text.includes('\n-')) {
      return text;
    }

    // Otherwise, split by lines and format each as a bullet point
    return text.split('\n').filter(line => line.trim()).map(line => {
      // If the line already starts with a number and colon (e.g., "1:"), strip that part
      const match = line.match(/^\s*\d+\s*:\s*(.*)/);
      return match ? `• ${match[1].trim()}` : `• ${line.trim()}`;
    }).join('\n');
  };

  return (
    <Paper
      elevation={selected ? 3 : 1}
      sx={{
        p: 3,
        mb: 2,
        transition: 'all 0.3s ease',
        border: selected ? '2px solid #3f51b5' : '1px solid rgba(0,0,0,0.1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
        },
      }}
    >
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box flex={1}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            {paper.title || 'Untitled Paper'}
          </Typography>

          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="body2" color="text.secondary" mr={2}>
              Year: {paper.year || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mr={2}>
              Citations: {paper.citationCount || 0}
            </Typography>
            {Array.isArray(paper.fieldsOfStudy) && paper.fieldsOfStudy.map((field) => (
              <Chip
                key={field}
                label={field}
                size="small"
                sx={{ mr: 0.5 }}
              />
            ))}
          </Box>

          {/* Score component - shown always */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Relevance Score: {showScore}/100
              </Typography>
              <Chip
                label={showScore >= 70 ? 'High' : showScore >= 40 ? 'Medium' : 'Low'}
                size="small"
                sx={{
                  bgcolor: showScore >= 70 ? 'rgba(76, 175, 80, 0.1)' : showScore >= 40 ? 'rgba(255, 152, 0, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                  color: showScore >= 70 ? '#4caf50' : showScore >= 40 ? '#ff9800' : '#f44336',
                  fontWeight: 600,
                }}
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={showScore}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(0,0,0,0.05)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: showScore >= 70 ? '#4caf50' : showScore >= 40 ? '#ff9800' : '#f44336',
                  borderRadius: 4,
                }
              }}
            />
          </Box>

          {showAbstract && (
            <>
              {/* Title row with expand/collapse button */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  p: 1,
                  borderRadius: '4px',
                  '&:hover': {
                    bgcolor: 'rgba(0,0,0,0.05)'
                  },
                  mt: 1
                }}
                onClick={toggleExpansion}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Abstract & AI Analysis
                </Typography>
                {isExpanded ?
                  <ExpandLessIcon fontSize="small" color="action" /> :
                  <ExpandMoreIcon fontSize="small" color="action" />
                }
              </Box>

              {/* Abstract and Reasoning content - only shown when expanded */}
              <Collapse in={isExpanded}>
                {/* Abstract section */}
                <Box sx={{ mt: 1, mb: 2, p: 1, bgcolor: 'rgba(0,0,0,0.01)', borderRadius: '4px' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.6
                    }}
                  >
                    {paper.abstract || 'No abstract available for this paper'}
                  </Typography>
                </Box>

                {/* AI Reasoning Section - shown if paper has ranking data */}
                {hasRankingData && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      AI Reasoning
                    </Typography>

                    <Grid container spacing={2}>
                      {paper.rankingData.argumentsFor && (
                        <Grid item xs={12} md={6}>
                          <Paper
                            sx={{
                              p: 2,
                              bgcolor: '#E8F5E9',
                              borderRadius: 1,
                              mb: 2
                            }}
                            elevation={0}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#2E7D32' }}>
                              <ThumbUp fontSize="small" sx={{ mr: 1 }} /> Arguments For Relevance
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {formatArguments(paper.rankingData.argumentsFor)}
                            </Typography>
                          </Paper>
                        </Grid>
                      )}

                      {paper.rankingData.argumentsAgainst && (
                        <Grid item xs={12} md={6}>
                          <Paper
                            sx={{
                              p: 2,
                              bgcolor: '#FFEBEE',
                              borderRadius: 1
                            }}
                            elevation={0}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#C62828' }}>
                              <ThumbDown fontSize="small" sx={{ mr: 1 }} /> Arguments Against Relevance
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {formatArguments(paper.rankingData.argumentsAgainst)}
                            </Typography>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}
              </Collapse>
            </>
          )}

          {/* Reasoning button - only show if reasoningAvailable is true but paper doesn't have rankingData */}
          {reasoningAvailable && socketId && !hasRankingData && (
            <Button
              size="small"
              startIcon={<PsychologyIcon />}
              onClick={handleOpenReasoningDialog}
              sx={{
                mt: 2,
                color: '#7209b7',
                '&:hover': {
                  backgroundColor: 'rgba(114, 9, 183, 0.08)'
                }
              }}
            >
              View AI Reasoning
            </Button>
          )}
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" ml={2}>
          <Checkbox
            checked={selected}
            onChange={() => onSelect(paper)}
            icon={<BookmarkIcon />}
            checkedIcon={<BookmarkedIcon />}
          />
          <Tooltip title="Open paper">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onOpenUrl(paper.url); }}
              sx={{ mt: 1 }}
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Citation (BibTeX)">
            <IconButton
              size="small"
              onClick={handleExportCitation}
              sx={{ mt: 1 }}
            >
              <FormatQuoteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Reasoning Dialog - Only render if socketId is available */}
      {socketId && (
        <PaperReasoningDialog
          open={reasoningDialogOpen}
          onClose={handleCloseReasoningDialog}
          paperIndex={index}
          socketId={socketId}
        />
      )}
    </Paper>
  );
}

export default React.memo(PaperCard); 